"use client";

/**
 * Shopping 도메인 TanStack Query 훅
 *
 * features 레이어: shared, ui, lib, hooks, workspace-pkg import 가능
 * Wave 1: factory 기반 단일화 + 검색 query key 분리
 */
import { useMemo } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createProductService, type GetProductsParams } from "@/shared/api/services/productService";
import { useBuyerService } from "@/shared/auth/hooks";

import { homeKeys } from "@/features/home/queries/keys";
import { favoriteKeys, productKeys } from "@/features/shopping/queries/keys";

export { productKeys };

/**
 * "인기" 배지 대상 상품 ID Set 조회 훅
 *
 * /home/badges 의 재구매 상품 (최근 60일, 다른 날 다시 산 사람 2명 이상).
 */
export function usePopularProductIds(): Set<number> {
  const service = useBuyerService(createProductService);

  const { data } = useQuery({
    queryKey: homeKeys.list({ type: "home-badges" }),
    queryFn: () => service.getHomeBadges(),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => new Set(data?.data?.popularProductIds ?? []), [data]);
}

/**
 * "인기 브랜드" 배지 대상 brand ID Set 조회 훅
 *
 * /home/badges 의 점수 순 상위 30개 브랜드 (하루 1번 갱신).
 */
export function usePopularBrandIds(): Set<number> {
  const service = useBuyerService(createProductService);

  const { data } = useQuery({
    queryKey: homeKeys.list({ type: "home-badges" }),
    queryFn: () => service.getHomeBadges(),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => new Set(data?.data?.popularBrandIds ?? []), [data]);
}

/**
 * 상품 목록 조회 훅
 */
export function useProducts(params?: GetProductsParams) {
  const service = useBuyerService(createProductService);

  return useQuery({
    queryKey: productKeys.list(params),
    queryFn: () =>
      service.getProducts(params as Record<string, string | number | boolean | undefined>),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      if (String(params?.idCategory) !== "14") return false;
      const transition = query.state.data?.data?.products?.season?.nextTransitionAt;
      const remaining = transition ? Date.parse(transition) - Date.now() : NaN;
      // Retry after a failed boundary refresh without polling an expired date in a tight loop.
      if (!Number.isFinite(remaining) || remaining <= 0) return 60_000;
      return Math.min(remaining + 100, 86_400_000);
    },
    refetchOnWindowFocus: String(params?.idCategory) === "14" ? "always" : true,
  });
}

/**
 * 상품 검색 훅
 *
 * 전용 query key 사용 (productKeys.search) — list와 캐시 충돌 방지
 * enabled 플래그로 게이팅: keyword가 비어있으면 실행하지 않음
 */
export function useProductSearch(keyword: string, page?: number) {
  const service = useBuyerService(createProductService);

  return useQuery({
    queryKey: [...productKeys.search(keyword), page] as const,
    queryFn: () => service.searchProducts({ keyword, page }),
    enabled: keyword.length > 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * 즐겨찾기 토글 뮤테이션 훅
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();
  const service = useBuyerService(createProductService);

  return useMutation({
    mutationFn: (productId: number) => service.toggleFavorite(productId),
    onSuccess: (_data, productId) => {
      void queryClient.invalidateQueries({ queryKey: favoriteKeys.check(String(productId)) });
      void queryClient.invalidateQueries({ queryKey: favoriteKeys.list() });
      void queryClient.invalidateQueries({ queryKey: productKeys.lists });
    },
  });
}
